import { Navigate } from '@tanstack/react-router';
import { useProfileQuery } from '@/api/queries/useProfile';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Header } from '@/layout/header/Header';
import { SeatOverrunMessage } from './SeatOverrunMessage';
import { SeatOverrunResolver } from './SeatOverrunResolver';

const SEAT_OVERRUN_POLL_INTERVAL_MS = 5 * 60_000;

/**
 * Lock screen for an org whose capped seat pools hold more logins than they
 * sell. Every write is refused with ErrorCode.SEAT_OVERRUN until a resolver
 * (org-manager or system-admin) suspends or deletes someone, so the whole app
 * is replaced by this page.
 */
export function SeatOverrunPage() {
  const slug = useAuthStore().slug();

  useDocumentTitle('organization.seatOverrun.title');

  const { data: profile } = useProfileQuery({
    refetchInterval: SEAT_OVERRUN_POLL_INTERVAL_MS,
    // Plain `true` would never fire under the client's 5-minute staleTime.
    refetchOnWindowFocus: 'always'
  });

  // Not redundant with the route guard: the guard runs once on load, while
  // this observer is what forwards a locked-out user home after somebody else
  // frees the seat — including after the resolver's own suspend/delete.
  if (profile && !profile.seatOverrun) {
    return <Navigate to="/$slug" params={{ slug }} />;
  }

  // Department managers can suspend/delete too, but only inside their own
  // department; these two roles can act on every row.
  const isResolver =
    profile?.data.userTypes.includes('org_manager') ||
    profile?.data.userTypes.includes('system_admin');

  return (
    // Renders outside the AppLayout chrome (the org is unusable while locked)
    // but mounts its own Header, so a locked-out user can still switch orgs or
    // log out.
    <div className="flex h-screen flex-col">
      <Header />
      <div className="flex min-h-0 flex-1 justify-center overflow-y-auto bg-sidebar p-8">
        {isResolver && profile ? (
          <SeatOverrunResolver currentUserId={profile.data._id} />
        ) : (
          <SeatOverrunMessage />
        )}
      </div>
    </div>
  );
}
